import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  TaskResponse,
  TaskRequest,
  TaskUpdateRequest,
  TaskFilterRequest,
  TaskCompleteRequest,
  TaskCancelRequest,
  TaskSnoozeRequest,
  TaskReopenRequest,
  TaskCompleteAndCreateNextRequest,
  TaskReassignRequest,
  TaskSetStatusRequest,
  TaskDuplicateCandidate,
  TaskDuplicateCandidatesParams,
  TaskTimelineEventResponse,
  PageResponse
} from '../models/task.model';

@Injectable({ providedIn: 'root' })
export class TaskApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) {}

  // POST /api/tasks/filter
  filterTasks(filter: TaskFilterRequest): Observable<PageResponse<TaskResponse>> {
    return this.http.post<PageResponse<TaskResponse>>(`${this.apiUrl}/tasks/filter`, filter);
  }

  // GET /api/tasks/{id}
  getTaskById(id: number): Observable<TaskResponse> {
    return this.http.get<TaskResponse>(`${this.apiUrl}/tasks/${id}`);
  }

  // POST /api/tasks
  createTask(req: TaskRequest): Observable<TaskResponse> {
    return this.http.post<TaskResponse>(`${this.apiUrl}/tasks`, req);
  }

  // PUT /api/tasks/{id}
  updateTask(id: number, req: TaskUpdateRequest): Observable<TaskResponse> {
    return this.http.put<TaskResponse>(`${this.apiUrl}/tasks/${id}`, req);
  }

  // DELETE /api/tasks/{id}
  deleteTask(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tasks/${id}`);
  }

  // POST /api/tasks/{id}/complete
  completeTask(id: number, req: TaskCompleteRequest = {}): Observable<TaskResponse> {
    return this.http.post<TaskResponse>(`${this.apiUrl}/tasks/${id}/complete`, req);
  }

  // POST /api/tasks/{id}/cancel
  cancelTask(id: number, req: TaskCancelRequest): Observable<TaskResponse> {
    return this.http.post<TaskResponse>(`${this.apiUrl}/tasks/${id}/cancel`, req);
  }

  // POST /api/tasks/{id}/snooze
  snoozeTask(id: number, req: TaskSnoozeRequest): Observable<TaskResponse> {
    return this.http.post<TaskResponse>(`${this.apiUrl}/tasks/${id}/snooze`, req);
  }

  // POST /api/tasks/{id}/reopen
  reopenTask(id: number, req: TaskReopenRequest): Observable<TaskResponse> {
    return this.http.post<TaskResponse>(`${this.apiUrl}/tasks/${id}/reopen`, req);
  }

  // POST /api/tasks/{id}/complete-and-create-next
  completeAndCreateNext(id: number, req: TaskCompleteAndCreateNextRequest): Observable<TaskResponse> {
    return this.http.post<TaskResponse>(`${this.apiUrl}/tasks/${id}/complete-and-create-next`, req);
  }

  // POST /api/tasks/{id}/reassign (INC 30 — reasignación individual con motivo)
  reassignTask(id: number, req: TaskReassignRequest): Observable<TaskResponse> {
    return this.http.post<TaskResponse>(`${this.apiUrl}/tasks/${id}/reassign`, req);
  }

  // PUT /api/tasks/{id}/status (INC 34 — cambiar a cualquier estado válido)
  setTaskStatus(id: number, req: TaskSetStatusRequest): Observable<TaskResponse> {
    return this.http.put<TaskResponse>(`${this.apiUrl}/tasks/${id}/status`, req);
  }

  // GET /api/tasks/{id}/subtasks
  getSubtasks(id: number): Observable<TaskResponse[]> {
    return this.http.get<TaskResponse[]>(`${this.apiUrl}/tasks/${id}/subtasks`);
  }

  // GET /api/tasks/duplicate-candidates - Candidatos a tarea duplicada por título (advertencia preventiva, nunca bloquea)
  // businessId se toma del JWT en el backend, NO se envía como query param.
  getDuplicateCandidates(params: TaskDuplicateCandidatesParams): Observable<TaskDuplicateCandidate[]> {
    let httpParams = new HttpParams().set('title', params.title);

    if (params.intIdCompany !== undefined) {
      httpParams = httpParams.set('intIdCompany', params.intIdCompany.toString());
    }
    if (params.intIdDeal !== undefined) {
      httpParams = httpParams.set('intIdDeal', params.intIdDeal.toString());
    }
    if (params.intIdLead !== undefined) {
      httpParams = httpParams.set('intIdLead', params.intIdLead.toString());
    }
    if (params.excludeId !== undefined) {
      httpParams = httpParams.set('excludeId', params.excludeId.toString());
    }

    return this.http.get<TaskDuplicateCandidate[]>(`${this.apiUrl}/tasks/duplicate-candidates`, { params: httpParams });
  }

  // Los selectores de usuario (responsable, seguidores, filtros) no viven aquí: usan
  // `UserDirectoryApiService` → GET /api/directory/users, el directorio compartido del CRM.

  // GET /api/tasks/{taskId}/timeline (INC 14 — historial de actividad, eventos NEWEST-FIRST).
  // businessId se toma del JWT en el backend.
  getTimeline(taskId: number): Observable<TaskTimelineEventResponse[]> {
    return this.http.get<TaskTimelineEventResponse[]>(`${this.apiUrl}/tasks/${taskId}/timeline`);
  }
}
